import { useEffect, useState } from 'react'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { IconSvg, PdfPreview } from 'components/common'

interface PolicyPdfModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	policyFileUrl?: string | null
	title?: string | null
}

export function PolicyPdfModal({ open, setOpen, policyFileUrl, title }: PolicyPdfModalProps) {
	const [pdfWidth, setPdfWidth] = useState<number>(800)

	useEffect(() => {
		const calculatePdfWidth = () => {
			// For mobile, use viewport width minus padding
			if (window.innerWidth < 640) {
				setPdfWidth(window.innerWidth - 64) // 32px padding on each side
			} else if (window.innerWidth < 1024) {
				// Tablet: use 90% of viewport width, max 800px
				setPdfWidth(Math.min(window.innerWidth * 0.9, 800))
			} else {
				// Desktop: use larger width for better viewing
				setPdfWidth(Math.min(window.innerWidth * 0.7, 1200))
			}
		}

		calculatePdfWidth()
		window.addEventListener('resize', calculatePdfWidth)
		return () => window.removeEventListener('resize', calculatePdfWidth)
	}, [])

	return (
		<ContentModal
			open={open}
			setOpen={setOpen}
			size="4xl"
			title={
				<div className="flex items-center justify-center gap-2">
					<IconSvg icon="user" aria-hidden="true" />
					<span className="text-center">{title || 'Opt-in Policy'}</span>
				</div>
			}
		>
			<div className="flex flex-col h-full max-h-[90vh]">
				<div className="flex-1 overflow-auto flex justify-center min-h-0">
					{policyFileUrl && (
						<div className="w-full">
							<PdfPreview file={`${import.meta.env.VITE_MEDIA_URL}/${policyFileUrl}`} width={pdfWidth} />
						</div>
					)}
				</div>
				<div className="flex-shrink-0 flex items-center justify-center mt-4 sm:mt-6 pt-4 border-t border-white/10">
					<button
						type="button"
						className="px-6 sm:px-8 rounded-3xl text-center transition-all duration-300 bg-secondary-button text-white font-bold block h-10 shadow-button backdrop-blur-lg hover:bg-secondary-button-hover disabled:bg-white/10 disabled:text-white-25 disabled:shadow-none"
						onClick={() => setOpen(false)}
					>
						Close
					</button>
				</div>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface PolicyPdfModalManagedProps {
	policyFileUrl?: string | null
	title?: string | null
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'policy-pdf': PolicyPdfModalManagedProps
	}
}

export function ManagedPolicyPdfModal({
	onClose,
	...rest
}: PolicyPdfModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<PolicyPdfModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
